import React, { useState } from 'react';
import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
const faqCategories = [{
  name: 'Orders & Shipping',
  faqs: [{
    question: 'How long does delivery take?',
    answer: 'Delivery typically takes 3-5 business days for major cities in Pakistan. Remote areas may take 5-7 business days. You can track your order using the tracking number provided in your confirmation email.'
  }, {
    question: 'Do you offer free shipping?',
    answer: 'Yes! We offer free shipping on all orders above PKR 5,000. For orders below this amount, a flat shipping fee of PKR 250-500 applies depending on your location.'
  }, {
    question: 'Can I track my order?',
    answer: "Absolutely! Once your order is shipped, you will receive a tracking number via email and SMS. You can use this to track your package on our website or the courier's website."
  }, {
    question: 'Do you deliver to all cities in Pakistan?',
    answer: 'Yes, we deliver nationwide across Pakistan. Delivery times may vary based on your location. Major cities receive faster delivery compared to remote areas.'
  }]
}, {
  name: 'Payment',
  faqs: [{
    question: 'What payment methods do you accept?',
    answer: 'We accept multiple payment methods including Cash on Delivery (COD), Credit/Debit Cards (Visa, MasterCard), and mobile wallet payments through JazzCash and EasyPaisa.'
  }, {
    question: 'Is Cash on Delivery available?',
    answer: 'Yes, Cash on Delivery (COD) is available for all orders across Pakistan. You can pay in cash when your order is delivered to your doorstep.'
  }, {
    question: 'Is online payment secure?',
    answer: 'Yes, all online payments are processed through secure, encrypted payment gateways. We use SSL encryption to protect your financial information.'
  }]
}, {
  name: 'Returns & Refunds',
  faqs: [{
    question: 'What is your return policy?',
    answer: 'We offer a 7-day return policy for all products. Items must be unused, in original packaging, and with all accessories. Contact our support team to initiate a return.'
  }, {
    question: 'How do I return a product?',
    answer: 'To return a product, contact our customer support team via email or phone. We will arrange for pickup of the item and process your refund within 5-7 business days after receiving the returned product.'
  }, {
    question: 'When will I receive my refund?',
    answer: 'Refunds are processed within 5-7 business days after we receive and inspect the returned item. The refund will be credited to your original payment method.'
  }]
}, {
  name: 'Products & Warranty',
  faqs: [{
    question: 'Are all products genuine?',
    answer: 'Yes, we guarantee 100% authentic products. All items are sourced directly from authorized distributors and come with official warranty.'
  }, {
    question: 'What warranty do products come with?',
    answer: 'Most products come with manufacturer warranty ranging from 6 months to 2 years depending on the product and brand. Warranty details are mentioned on each product page.'
  }, {
    question: 'How do I claim warranty?',
    answer: 'To claim warranty, contact our support team with your order number and proof of purchase. We will guide you through the warranty claim process with the manufacturer.'
  }]
}];
export function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
  })).filter(category => category.faqs.length > 0);
  return <div className="min-h-screen bg-gray-50" data-id="element-1282">
      {/* Hero */}
      <div className="bg-primary-600 py-16" data-id="element-1283">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1284">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1285">
            Frequently Asked Questions
          </h1>
          <p className="text-primary-100 max-w-2xl mx-auto mb-8" data-id="element-1286">
            Find answers to common questions about orders, shipping, payments,
            and more.
          </p>
          <div className="max-w-xl mx-auto" data-id="element-1287">
            <Input type="search" placeholder="Search for answers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-5 h-5" data-id="element-1289" />} className="bg-white" data-id="element-1288" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1290">
        {filteredCategories.length === 0 ? <div className="text-center py-12" data-id="element-1291">
            <p className="text-gray-500" data-id="element-1292">
              No results found for "{searchQuery}"
            </p>
          </div> : <div className="space-y-8" data-id="element-1293">
            {filteredCategories.map(category => <div key={category.name} data-id="element-1294">
                <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1295">
                  {category.name}
                </h2>
                <div className="space-y-3" data-id="element-1296">
                  {category.faqs.map((faq, index) => {
              const itemId = `${category.name}-${index}`;
              const isOpen = openItems[itemId];
              return <div key={index} className="bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden" data-id="element-1297">
                        <button onClick={() => toggleItem(itemId)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" aria-expanded={isOpen} data-id="element-1298">
                          <span className="font-medium text-gray-900 pr-4" data-id="element-1299">
                            {faq.question}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1300" />
                        </button>
                        {isOpen && <div className="px-4 pb-4" data-id="element-1301">
                            <p className="text-gray-600" data-id="element-1302">{faq.answer}</p>
                          </div>}
                      </div>;
            })}
                </div>
              </div>)}
          </div>}

        {/* Contact CTA */}
        <div className="mt-12 bg-primary-50 rounded-xl p-8 text-center" data-id="element-1303">
          <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1304">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-1305">
            Can't find the answer you're looking for? Our support team is here
            to help.
          </p>
          <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors" data-id="element-1306">
            Contact Support
          </a>
        </div>
      </div>
    </div>;
}